<template>
  <div class="mypage-ticket-past-pane">
    <div
      class="mypage-ticket-past-pane__container"
      :class="{ 'mypage-ticket-past-pane__container--pc': isPc }"
    >
      <div class="mypage-ticket-past-pane__ticket">
        <div class="mypage-ticket-past-pane__header">
          <p class="mypage-ticket-past-pane__place">{{ targetTicketEvent.eventName }}</p>
          <p class="mypage-ticket-past-pane__date">{{ displayEventDate }}</p>
        </div>
        <div class="mypage-ticket-past-pane__background" :style="pastScreenBgImageStyle"></div>
        <div class="mypage-ticket-past-pane__icons">
          <div class="mypage-ticket-past-pane__stamp" v-if="isEntered"></div>
          <div class="mypage-ticket-past-pane__circuit">
            <motegi-parts v-if="targetTicketEvent.stadiumId === 'TWIN_RING_MOTEGI'" />
            <fuji-parts v-if="targetTicketEvent.stadiumId === 'FUJI_SPEEDWAY'" />
            <sugo-parts v-if="targetTicketEvent.stadiumId === 'SPORTSLAND_SUGO'" />
            <autopolice-parts v-if="targetTicketEvent.stadiumId === 'AUTOPOLIS'" />
            <suzuka-parts v-if="targetTicketEvent.stadiumId === 'SUZUKA_CIRCUIT'" />
          </div>
        </div>
      </div>
      <div class="mypage-ticket-past-pane__buttons" v-if="isPc && isOpen">
        <ul class="mypage-ticket-past-pane__buttons-list">
          <li class="mypage-ticket-past-pane__buttons-item">
            <atom-button :linkText="$tc('MypagePage.MypageTicketPastPage.download')" />
          </li>
          <li class="mypage-ticket-past-pane__buttons-item">
            <atom-button
              :linkText="$tc('MypagePage.MypageTicketPastPage.post.x')"
              :colorType="'secondary'"
            />
          </li>
          <li class="mypage-ticket-past-pane__buttons-item">
            <atom-button
              :linkText="$tc('MypagePage.MypageTicketPastPage.post.instagram')"
              :colorType="'secondary'"
            />
          </li>
          <li class="mypage-ticket-past-pane__buttons-item">
            <atom-button
              :linkText="$tc('MypagePage.MypageTicketPastPage.edit')"
              :colorType="'secondary'"
            />
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, Ref } from '@vue/composition-api'
import StoreUtil from '@/store/StoreUtil'
import DigitalTicketDataDocument from '@/store/stores/collectionModule/documents/digitalTicket/DigitalTicketDataDocument'
import useDisplayDependingOnLang from '@/components/hook/useDisplayDependingOnLang'
import usePurchasedTicketData from '@/components/hook/ticket/usePurchasedTicketData'
import FujiParts from '@/components/common/circuit/FujiParts.vue'
import SuzukaParts from '@/components/common/circuit/SuzukaParts.vue'
import SugoParts from '@/components/common/circuit/SugoParts.vue'
import AutopoliceParts from '@/components/common/circuit/AutopoliceParts.vue'
import MotegiParts from '@/components/common/circuit/MotegiParts.vue'
import DeviceInfo from '@/util/DeviceInfo'
import AtomButton from '@/components/atoms/AtomButton.vue'

export default defineComponent({
  name: 'MypageTicketPastPane',
  components: {
    AtomButton,
    MotegiParts,
    AutopoliceParts,
    SugoParts,
    SuzukaParts,
    FujiParts,
  },
  setup() {
    const mypageTicketPageStore = StoreUtil.useStore('MypageTicketPageStore')
    const mypagePageStore = StoreUtil.useStore('MypagePageStore')
    const { selectedPastTicketInfo, digitalTicketsByUniqueKey, digitalTicketImagesByUserImageId } =
      mypageTicketPageStore
    const { ownOrganization } = mypagePageStore
    const { isEnteredTargetEventDate } = usePurchasedTicketData()
    const { getDisplayDateJa } = useDisplayDependingOnLang()

    const isPc = DeviceInfo.isForPC()
    // TODO: SNSシェア機能を公開次第、こちらのフラグを削除する
    const isOpen = false

    /**
     * 対象チケットのイベント
     */
    const targetTicketEvent = computed(
      () =>
        digitalTicketsByUniqueKey.value?.[selectedPastTicketInfo.value?.ticketEventUniqueKey ?? ''],
    )

    /**
     * 過去のチケット背景画像
     */
    const pastScreenBgImageStyle = computed(() => ({
      '--img': `url(${
        digitalTicketImagesByUserImageId.value[targetTicketEvent.value.userImageId ?? '']?.path
      })`,
    }))

    /**
     * イベント開催日
     */
    const displayEventDate = computed(() =>
      selectedPastTicketInfo.value?.day
        ? getDisplayDateJa(targetTicketEvent.value.openingDates?.[selectedPastTicketInfo.value.day])
        : '',
    )

    /**
     * 入場済みかどうか
     */
    const isEntered = computed(() =>
      isEnteredTargetEventDate(
        targetTicketEvent.value,
        ownOrganization.value,
        selectedPastTicketInfo.value?.day,
      ),
    )

    return {
      pastScreenBgImageStyle,
      targetTicketEvent: targetTicketEvent as Ref<DigitalTicketDataDocument>,
      displayEventDate,
      isEntered,
      isPc,
      isOpen,
    }
  },
})
</script>

<style scoped lang="scss">
.mypage-ticket-past-pane {
  position: relative;
  width: 100%;
  height: calc(100svh - #{$headerPaneHeight} - #{$subHeaderPaneHeight});
  overflow-y: auto;

  &__container {
    height: 100%;

    &--pc {
      display: flex;
      gap: 24px;
      align-items: center;
      justify-content: center;
      width: 800px;
      height: auto;
      padding: 24px 0;
      margin-right: auto;
      margin-left: auto;
    }
  }

  &__ticket {
    position: relative;
    width: 100%;
    height: 100%;

    .mypage-ticket-past-pane__container--pc & {
      width: 375px;
      height: 676px;
    }

    &::before {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 2;
      width: 100%;
      height: 140px;
      content: '';
      background: linear-gradient(180deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 100%);
    }
  }

  &__header {
    position: relative;
    z-index: 3;
    padding: 16px;
    color: $white;
    text-align: right;
  }

  &__place {
    font-family: $fontHeading;
    font-size: 18px;
    font-weight: 600;
    letter-spacing: 0.04em;
  }

  &__date {
    position: relative;
    margin-top: 14px;
    font-family: $fontHeading;
    font-size: 28px;
    font-weight: 600;
    line-height: 1.1;
    letter-spacing: 0.04em;

    &::before {
      position: absolute;
      top: -8px;
      right: 0;
      width: 24px;
      height: 2px;
      content: '';
      background: linear-gradient(
        84.94deg,
        #00ffba -121.14%,
        #00ffba -121.14%,
        rgba(0, 255, 186, 0.5) -80.24%,
        #00ffba -34.6%,
        rgba(0, 255, 186, 0.5) 10.88%,
        #00ffba 51.93%,
        rgba(0, 255, 186, 0.5) 91.87%
      );
    }
  }

  &__background {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    width: 100%;
    height: 100%;
    background-image: var(--img);
    background-repeat: no-repeat;
    background-position: center top;
    background-size: cover;
  }

  &__icons {
    position: absolute;
    top: -14px;
    left: -20px;
    z-index: 2;
    width: 156px;
    height: 156px;
    box-shadow: 0 1px 0 0 rgba(#000000, 0.05);
  }

  &__stamp {
    position: absolute;
    top: 14px;
    left: 20px;
    width: 136px;
    height: 143px;
    background-image: url('@/assets/img/TicketPage/stamp_past.png');
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;
  }

  &__circuit {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 110px;
    height: 82px;
    color: $white;
    opacity: 0.1;
    transform: translate(-50%, -50%);
  }

  &__buttons {
    width: 347px;

    &-list {
      display: flex;
      gap: 12px;
      flex-direction: column;
    }
  }
}
</style>
